import Image from "next/image";
import React from "react";

import bullet from "@/assets/img/how-it-works/bullet.svg";
import line from "@/assets/img/how-it-works/line_divider.svg";
import theme from "@/styles/themes/theme";
import { GenericCard } from "@/types/contentful";
import { Box, Button, Stack, Typography, useMediaQuery } from "@mui/material";
import { ArrowUpRight } from "@phosphor-icons/react";
import Link from "next/link";
import { Document, MARKS } from "@contentful/rich-text-types";

import { styles } from "./styles";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";

export type SliderItemProps = GenericCard & {
  onClick?: () => void;
};

export const CoverPartnersInformation: React.FC<SliderItemProps> = ({
  onClick,
  backgroundColor,
  subtitle,
  image,
  textColor,
  secondaryImage,
  thirdImage,
  url,
  informative,
  buttonTitle,
  buttonColor
}) => {
  const isSm = useMediaQuery(theme.breakpoints.down("md"));
  const isXs = useMediaQuery(theme.breakpoints.down("xs"));
  const isXl = useMediaQuery(theme.breakpoints.down("xl"));

  const borderBottomLeftRadius = isXs ? "5rem" : isSm ? "12rem" : isXl ? "15rem" : "20rem";

  const darkenColor = (color: string, percent: number): string => {
    const num = parseInt(color.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) - amt;
    const G = ((num >> 8) & 0x00ff) - amt;
    const B = (num & 0x0000ff) - amt;

    return `#${(
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
      (B < 255 ? (B < 1 ? 0 : B) : 255)
    )
      .toString(16)
      .slice(1)
      .toUpperCase()}`;
  };

  return (
    <Stack
      direction={{ xs: "column-reverse", md: "row" }}
      position="relative"
      bgcolor={isSm ? "#ffffff" : backgroundColor}
      minWidth="100vw"
      height="100%"
    >
      <Stack direction="column" flex={1}>
        <Box bgcolor="#ffffff">
          <Stack
            display="flex"
            justifySelf="center"
            textAlign={isSm ? "start" : "start"}
            alignItems={isSm ? "start" : "start"}
            height="auto"
            sx={isSm ? { marginLeft: "20px" } : { marginLeft: "50px" }}
          >
            {secondaryImage ? (
              <Image
                src={secondaryImage}
                alt=""
                width={isSm ? 150 : 200}
                height={isSm ? 70 : 109}
                style={{ marginTop: "20px", marginBottom: "20px", objectFit: "contain" }}
              />
            ) : null}
          </Stack>
          <Box position="relative" lineHeight={0.5}>
            <Image src={line} alt="" style={{ width: "100%", height: "auto" }} />
            <Image
              src={bullet}
              alt=""
              style={{
                width: 32,
                height: 32,
                position: "absolute",
                right: -18,
                bottom: -14,
                display: isSm ? "none" : "inherit",
                pointerEvents: "none",
                zIndex: "2"
              }}
            />
          </Box>
        </Box>

        <Box bgcolor={backgroundColor}>
          <Stack ml={{ lg: 8, md: 2, sm: 2, xs: 2 }} flex={1} mt={{ lg: 10, md: 4, sm: 10, xs: 10 }} mb={{ sm: 2 }}>
            <Typography
              component="div"
              color={textColor || "#0045B5"}
              fontWeight={600}
              fontSize={38}
              style={{ wordBreak: "break-word" }}
              lineHeight="38px"
              whiteSpace="pre-line"
              sx={{ marginRight: 3 }}
            >
              {subtitle}
            </Typography>

            {informative ? (
              <Typography
                component="div"
                color={textColor || "#0045B5"}
                fontSize={19}
                lineHeight="40px"
                maxWidth={{
                  xs: "80vw",
                  md: "40rem",
                  lg: "32rem",
                  xl: "45rem"
                }}
                style={{ wordBreak: "break-word" }}
                marginTop="24px"
                marginLeft="5px"
              >
                {typeof informative === "object"
                  ? documentToReactComponents(informative as Document, {
                      renderMark: {
                        [MARKS.BOLD]: (text) => <span style={{ fontWeight: 600 }}>{text}</span>
                      }
                    })
                  : informative}
              </Typography>
            ) : null}

            <Button
              endIcon={<ArrowUpRight size={20} />}
              onClick={onClick}
              sx={{
                alignSelf: "start",
                textTransform: "none",
                fontSize: "18px",
                lineHeight: "21px",
                color: textColor || "#0045B5",
                paddingLeft: 0,
                marginTop: { xs: "16px", md: "24px" },
                marginBottom: { xs: "83px", md: "80px" },
                backgroundColor: buttonColor,
                "&:hover": {
                  backgroundColor: darkenColor(buttonColor?.toString() ?? "#0a266e", 5)
                },
                "&:disabled": {
                  backgroundColor: "#ccdaf0"
                }
              }}
              css={styles.box__button}
            >
              <Link href={url} passHref target={url ? "_blank" : "_self"}>
                {buttonTitle}
              </Link>
            </Button>
          </Stack>
        </Box>
      </Stack>

      <Box
        minHeight="300px"
        position="relative"
        minWidth="50vw"
        style={{
          borderBottomLeftRadius: borderBottomLeftRadius,
          border: "3px solid",
          borderBottomColor: "#0045B5",
          borderLeftColor: "#0045B5",
          borderTopColor: "#0045B5",
          borderRightWidth: 0,
          overflow: "hidden"
        }}
        flex={{ xs: 0, sm: 0, md: 1 }}
        bgcolor={isSm ? "#ffffff" : backgroundColor}
      >
        <Box
          position="absolute"
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            minHeight: {
              xs: "50vh",
              md: "65vh",
              xl: "74vh"
            }
          }}
        >
          <Box
            component="img"
            src={image}
            alt=""
            position="absolute"
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover"
            }}
          />
        </Box>

        {thirdImage && !isSm ? (
          <Box
            component="img"
            src={thirdImage}
            alt=""
            sx={{
              position: "absolute",
              bottom: 50,
              right: 50,
              width: 250,
              height: 100
            }}
          />
        ) : null}
      </Box>
    </Stack>
  );
};
